import { memo } from 'preact/compat'
import type { ComponentChildren } from 'preact'
import { useLocation } from 'preact-iso'
import type { DashboardHottestCompanyItemDto } from '@/types/dashboard'
import { Flame } from 'lucide-preact'
import { shortAgo } from '@/utils/time'
import { CompanyAvatar } from '@/components/common/Avatar'
import { useCompany } from '@/hooks/useCompany'
import { routes } from '@/utils/routes'

interface HottestCompaniesTileProps {
    companies: DashboardHottestCompanyItemDto[] | null | undefined
    workspaceId?: string
    limit?: number
    className?: string
    loading?: boolean
    error?: string | null
    /** The server could not compute this section — shown as broken, not as empty. */
    failed?: boolean
    /** Suppresses navigation while the dashboard is showing mock data. */
    mock?: boolean
}

function computeHeatLevel(items: DashboardHottestCompanyItemDto[]): number {
    if (!items.length) return 0
    const max = Math.max(...items.map(i => i.ConversionCount)) || 1
    return max
}

export const HottestCompaniesTile = memo(({ companies, workspaceId, limit = 5, className = '', loading = false, error = null, failed = false, mock = false }: HottestCompaniesTileProps) => {
    const maxConv = companies ? computeHeatLevel(companies) : 0
    const { companyId: clientCompanyId } = useCompany()
    const location = useLocation()

    // CompanyId here is the canonical Company document id, so it addresses the profile directly —
    // unlike CompanySignalDto.CompanyId on the Companies grids, which is a grouping key.
    const canNavigate = !mock && !!clientCompanyId && !!workspaceId
    const hrefFor = (targetCompanyId: string) =>
        canNavigate ? routes.scoped(clientCompanyId!, workspaceId!).companyProfile(targetCompanyId) : undefined

    return (
        <div className={`tile flex flex-col ${className}`.trim()}>
            <div className="flex items-start justify-between mb-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 leading-tight">
                    Hottest Companies
                </h3>
                <Flame className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            {loading && (
                <ul className="space-y-3 mt-1">
                    {Array.from({ length: limit }).map((_, i) => (
                        <li key={i} className="flex items-center gap-3 row-hover">
                            <div className="w-10 h-10 rounded-md bg-shimmer-light animate-shimmer" />
                            <div className="flex-1 space-y-2">
                                <div className="h-3 w-40 rounded bg-shimmer-light animate-shimmer" />
                                <div className="h-3 w-24 rounded bg-shimmer-light animate-shimmer" />
                            </div>
                            <div className="h-5 w-14 rounded bg-shimmer-light animate-shimmer" />
                        </li>
                    ))}
                </ul>
            )}
            {!loading && error && (
                <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
            )}
            {!loading && !error && failed && (
                <div className="text-sm text-red-600 dark:text-red-400">Couldn't load hottest companies</div>
            )}
            {!loading && !error && !failed && companies && companies.length === 0 && (
                <div className="text-sm text-slate-500 dark:text-slate-400">No recent company activity</div>
            )}
            {!loading && !error && !failed && companies && companies.length > 0 && (
                <ul className="space-y-3 mt-1">
                    {companies.map((c: DashboardHottestCompanyItemDto) => {
                        const intensity = maxConv ? c.ConversionCount / maxConv : 0
                        // Map intensity to a pill variant: low=flat, mid=up, high=up plus stronger color (inline style)
                        const pillClass = intensity >= 0.66 ? 'pill-up' : intensity >= 0.33 ? 'pill-flat' : 'pill-flat'
                        const bgStyle = intensity >= 0.66 ? { filter: 'brightness(1)' } : intensity >= 0.33 ? { opacity: 0.85 } : { opacity: 0.65 }
                        return (
                            <RowShell
                                key={c.CompanyId}
                                href={hrefFor(c.CompanyId)}
                                label={`Open ${c.CompanyName || 'company'} profile`}
                                onNavigate={(target) => location.route(target)}
                            >
                                <CompanyAvatar
                                    companyName={c.CompanyName || 'Unknown'}
                                    logoUrl={c.LogoUrl}
                                    size="lg"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate" title={c.CompanyName || undefined}>{c.CompanyName || 'Unknown Company'}</div>
                                    <div className="text-2xs uppercase tracking-wide text-slate-500 dark:text-slate-400 truncate" title={(c.PrimaryIndustry || '') + (c.StaffCountRange ? ' · ' + c.StaffCountRange : '')}>
                                        {(c.PrimaryIndustry || '—')}{c.StaffCountRange ? ` · ${c.StaffCountRange}` : ''}
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                    <span className={`pill ${pillClass} text-xs2`} style={bgStyle} title="Recent conversion volume">
                                        {c.ConversionCount.toLocaleString()} conv
                                    </span>
                                    <span className="text-3xs font-medium text-slate-600 dark:text-slate-400" title={c.LastConversionUtc ? new Date(c.LastConversionUtc).toLocaleString() : ''}>
                                        {c.LastConversionUtc ? shortAgo(c.LastConversionUtc) : '—'}
                                    </span>
                                </div>
                            </RowShell>
                        )
                    })}
                </ul>
            )}
        </div>
    )
})

/**
 * A tile row that is a real link when there is somewhere to go, and a plain row when there isn't.
 *
 * An anchor rather than an onClick handler on the <li>, so middle-click, cmd-click and "open in
 * new tab" behave — an operator lives in this dashboard with several tabs open, and a row that
 * only responds to a plain left click quietly takes that away. Matches CompanyNameCell, which
 * makes the same choice on the Companies grids.
 */
function RowShell({
    href,
    label,
    onNavigate,
    children,
}: {
    href?: string
    label: string
    onNavigate: (href: string) => void
    children: ComponentChildren
}) {
    if (!href) {
        return <li className="flex items-center gap-3 row-hover">{children}</li>
    }

    return (
        <li>
            <a
                href={href}
                aria-label={label}
                onClick={(e: MouseEvent) => {
                    // Leave modifier and non-primary clicks to the browser; the href is real.
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
                    e.preventDefault()
                    onNavigate(href)
                }}
                // No focus ring of its own. `.row-hover:focus-visible` owns the indicator app-wide,
                // and it is neutral because brand green is ~1.8:1 on white — under the 3:1 WCAG 2.2
                // SC 1.4.11 asks for (ORB-625). A per-element ring here is the same specificity and
                // sits in the utilities layer, so it silently won and put the failing colour back on
                // the one tile this ticket made clickable. `row-hover` also supplies `rounded-md`.
                className="flex items-center gap-3 row-hover cursor-pointer no-underline text-inherit"
            >
                {children}
            </a>
        </li>
    )
}
